import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Users, Send, UserX, Clock } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';

const REASONS = { nunca_acessou: 'Nunca acessou', perfil_incompleto: 'Perfil incompleto' };
const FIELDS = { cargo: 'cargo', escolaridade: 'escolaridade', experiencia: 'experiência', curriculo: 'currículo' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-slate-500"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
  </div>
);

const RecentTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="profile-reminders-recent-table">
      <thead><tr className="text-left text-slate-500 border-b">
        <th className="py-2 pr-3">Candidato</th><th className="pr-3">Motivo</th><th className="pr-3">Faltava</th>
        <th className="pr-3">Envio</th><th className="pr-3">Data</th><th>Status</th>
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b last:border-0" data-testid={`profile-reminder-row-${r.id}`}>
            <td className="py-2 pr-3"><div className="font-medium">{r.nome || '—'}</div><div className="text-xs text-slate-500">{r.email}</div></td>
            <td className="pr-3">{REASONS[r.reason] || r.reason}</td>
            <td className="pr-3 text-xs">{(r.missing || []).map((m) => FIELDS[m] || m).join(', ')}</td>
            <td className="pr-3">{r.number}º</td>
            <td className="pr-3 whitespace-nowrap">{fmt(r.sent_at)}</td>
            <td><Badge variant={r.status === 'enviado' ? 'default' : 'destructive'}>{r.status}</Badge></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ProfileRemindersCard = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminAPI.getProfileReminders().then((r) => setData(r.data)).catch(() => toast.error('Erro ao carregar lembretes'));
  useEffect(() => { load(); }, []);

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      await adminAPI.updateProfileReminderSettings({ enabled });
      toast.success(enabled ? 'Lembretes automáticos ativados' : 'Lembretes automáticos desativados');
      await load();
    } catch { toast.error('Erro ao salvar'); } finally { setSaving(false); }
  };

  if (!data) return null;
  const lastRun = data.runs?.[0];
  return (
    <Card className="border-slate-200 mt-8" data-testid="profile-reminders-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-cyan-600" />Lembretes de Perfil Incompleto</CardTitle>
            <CardDescription>
              E-mail automático para candidatos que não preencheram o perfil ou nunca acessaram. {data.schedule}; no máximo {data.max_reminders} lembretes por candidato, com {data.interval_days} dias de intervalo.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-slate-600" data-testid="profile-reminders-status">{data.enabled ? 'Ativado' : 'Desativado'}</span>
            <Switch checked={data.enabled} disabled={saving} onCheckedChange={toggle} data-testid="profile-reminders-toggle" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat icon={Users} label="Perfis incompletos" value={data.incomplete_count} testid="stat-incomplete" />
          <Stat icon={UserX} label="Nunca acessaram" value={data.never_accessed_count} testid="stat-never-accessed" />
          <Stat icon={Clock} label="Recebem no próximo envio" value={data.eligible_count} testid="stat-eligible" />
          <Stat icon={Send} label="E-mails enviados" value={data.sent_total} testid="stat-sent" />
          <Stat icon={Mail} label="Pediram para parar" value={data.opted_out_count} testid="stat-opted-out" />
        </div>
        <p className="text-xs text-slate-500" data-testid="profile-reminders-last-run">
          Último envio: {lastRun ? `${fmt(lastRun.started_at)} · ${lastRun.status} · ${lastRun.sent ?? 0} enviados` : 'ainda não houve envio'}
        </p>
        {data.recent?.length > 0 && <RecentTable rows={data.recent} />}
      </CardContent>
    </Card>
  );
};
